/**
 * Live lesson room — student side. The mic streams the live pitch trace to
 * the coach over the lesson WebSocket; when the coach pushes an exercise it
 * runs right here (count-in, reference audio, scrolling target) and the
 * result score is reported back. Audio/video runs on the built-in WebRTC
 * call — and the room carries what a video call can't: the pitch data.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  renderCallResponse,
  renderPattern,
  scoreTrace,
  type ExercisePattern,
  type RenderedExercise,
  type TracePoint,
} from '@solfo/shared';
import { api } from '../../lib/api.ts';
import { openLiveSocket, sendLive, type LiveMessage } from '../../lib/live.ts';
import { isRtcSignal, useVideoCall } from '../../lib/rtc.ts';
import { t } from '../../lib/i18n.ts';
import { Button, Card, ErrorText, Spinner, Tag } from '../../lib/ui.tsx';
import { MicSession } from '../../audio/mic.ts';
import { audioContext, playExercise, type PlaybackHandle } from '../../audio/synth.ts';
import PitchCanvas from '../../components/PitchCanvas.tsx';
import LiveTraceCanvas from '../../components/LiveTraceCanvas.tsx';
import VideoCallPanel from '../../components/VideoCallPanel.tsx';

interface Lesson {
  id: string;
  startsAt: string;
  coachName?: string;
}

interface ExerciseInfo {
  id: string;
  name: string;
  mode?: string;
  patternJson: string | null;
}

type Phase = 'idle' | 'joining' | 'live' | 'running';

export default function StudentLiveLesson() {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('idle');
  const [connected, setConnected] = useState(false);
  const [coachHere, setCoachHere] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [lesson, setLesson] = useState<Lesson | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const micRef = useRef<MicSession | null>(null);
  const anchorRef = useRef(0); // freeform time base (AudioContext clock)
  const outboxRef = useRef<TracePoint[]>([]);
  const flushTimerRef = useRef(0);

  const [freePoints, setFreePoints] = useState<TracePoint[]>([]);
  const freeRef = useRef<TracePoint[]>([]);

  // exercise run
  const [runInfo, setRunInfo] = useState<{ exercise: ExerciseInfo; rendered: RenderedExercise; isEcho: boolean } | null>(null);
  const runInfoRef = useRef<typeof runInfo>(null);
  const handleRef = useRef<PlaybackHandle | null>(null);
  const rafRef = useRef(0);
  const runTraceRef = useRef<TracePoint[]>([]);
  const [runTrace, setRunTrace] = useState<TracePoint[]>([]);
  const [runTime, setRunTime] = useState(0);
  const [lastScore, setLastScore] = useState<number | null>(null);
  /** Coach-controlled: whether this device shows the pitch visualizer. */
  const [showVisualizer, setShowVisualizer] = useState(true);

  // bidirectional video call — the student is the polite peer
  const call = useVideoCall({
    enabled: phase === 'live' || phase === 'running',
    otherPresent: coachHere,
    polite: true,
    send: (msg) => sendLive(wsRef.current, msg),
  });
  const { handleSignal, announceState } = call;
  useEffect(() => {
    if (coachHere) announceState();
  }, [coachHere, announceState]);

  useEffect(() => {
    void api<Lesson[]>('/api/scheduling/lessons')
      .then((ls) => setLesson(ls.find((l) => l.id === lessonId) ?? null))
      .catch(() => {});
  }, [lessonId]);

  async function join() {
    setPhase('joining');
    setError(null);
    try {
      const mic = new MicSession();
      await mic.open();
      micRef.current = mic;

      const ws = openLiveSocket(lessonId!);
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => setConnected(false);
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data as string) as LiveMessage;
          if (isRtcSignal(msg)) {
            void handleSignal(msg);
            return;
          }
          if (msg.type === 'presence') setCoachHere(msg.coach);
          if (msg.type === 'exercise') void startExercise(msg.exerciseId, msg.rootMidi, msg.tempoBpm);
          if (msg.type === 'visualizer') setShowVisualizer(msg.show);
        } catch {
          /* ignore */
        }
      };

      startFreeform();
      // batch trace points out every 150ms
      flushTimerRef.current = window.setInterval(() => {
        if (outboxRef.current.length > 0) {
          sendLive(wsRef.current, { type: 'trace', points: outboxRef.current });
          outboxRef.current = [];
        }
      }, 150);
      setPhase('live');
    } catch (err) {
      setError(err);
      setPhase('idle');
      leaveCleanup();
    }
  }

  function startFreeform() {
    const mic = micRef.current;
    if (!mic) return;
    const ac = audioContext();
    anchorRef.current = ac.currentTime;
    const latencyMs = Number(localStorage.getItem('solfo.latencyMs') ?? 0);
    mic.stopTracking();
    mic.startTracking(
      () => audioContext().currentTime - anchorRef.current,
      (p) => {
        outboxRef.current.push(p);
        freeRef.current = [...freeRef.current, p].slice(-3000);
        setFreePoints(freeRef.current);
      },
      latencyMs,
    );
  }

  async function startExercise(exerciseId: string, rootMidi: number, tempoBpm: number) {
    if (runInfoRef.current) return; // already running
    try {
      const exercise = await api<ExerciseInfo>(`/api/exercises/${exerciseId}`);
      if (!exercise.patternJson) return;
      const pattern = JSON.parse(exercise.patternJson) as ExercisePattern;
      const isEcho = exercise.mode === 'call_response';
      const echo = isEcho ? renderCallResponse(pattern, rootMidi, tempoBpm) : null;
      const playback = echo ? echo.playback : renderPattern(pattern, rootMidi, tempoBpm);
      const target = echo ? echo.target : playback;

      const info = { exercise, rendered: target, isEcho };
      runInfoRef.current = info;
      setRunInfo(info);
      runTraceRef.current = [];
      setRunTrace([]);
      setLastScore(null);

      const handle = playExercise(playback, { tempoBpm, countInBeats: 4 });
      handleRef.current = handle;
      sendLive(wsRef.current, { type: 'exercise-started', exerciseId, countInSec: handle.countInSec });

      // retarget the mic to the exercise clock
      const mic = micRef.current!;
      const ac = audioContext();
      const latencyMs = Number(localStorage.getItem('solfo.latencyMs') ?? 0);
      mic.stopTracking();
      mic.startTracking(
        () => audioContext().currentTime - handle.startTime,
        (p) => {
          outboxRef.current.push(p);
          runTraceRef.current.push(p);
        },
        latencyMs,
      );

      const totalSec = playback.totalSec;
      setPhase('running');
      const tick = () => {
        const now = ac.currentTime - handle.startTime;
        setRunTime(now);
        setRunTrace([...runTraceRef.current]);
        if (now >= totalSec + 0.4) {
          finishExercise();
          return;
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (err) {
      setError(err);
      runInfoRef.current = null;
      setRunInfo(null);
    }
  }

  function finishExercise() {
    cancelAnimationFrame(rafRef.current);
    handleRef.current?.stop();
    const info = runInfoRef.current;
    let score: number | null = null;
    if (info && info.rendered.notes.some((n) => n.midi !== null)) {
      score = scoreTrace(runTraceRef.current, info.rendered).score;
    }
    setLastScore(score);
    sendLive(wsRef.current, { type: 'exercise-finished', score });
    runInfoRef.current = null;
    setRunInfo(null);
    freeRef.current = [];
    setFreePoints([]);
    setPhase('live');
    startFreeform();
  }

  function leaveCleanup() {
    cancelAnimationFrame(rafRef.current);
    clearInterval(flushTimerRef.current);
    handleRef.current?.stop();
    micRef.current?.close();
    micRef.current = null;
    wsRef.current?.close();
    wsRef.current = null;
  }
  useEffect(() => leaveCleanup, []);

  const freeNow = freePoints.length > 0 ? freePoints[freePoints.length - 1]!.t : 0;

  return (
    <div className="mx-auto flex min-h-dvh max-w-lg flex-col">
      <header className="flex items-center justify-between px-4 py-3">
        <button
          onClick={() => {
            leaveCleanup();
            navigate('/app/lessons');
          }}
          className="text-sm text-slate-500"
        >
          ← {t('live.leave', 'Leave')}
        </button>
        <h1 className="text-sm font-semibold">🔴 {t('live.title', 'Live lesson')}</h1>
        <span className="w-12" />
      </header>

      <main className="flex-1 space-y-4 px-4 pb-8">
        <div className="flex flex-wrap items-center gap-2">
          <Tag tone={connected ? 'green' : 'default'}>{connected ? t('live.connected', 'connected') : 'offline'}</Tag>
          <Tag tone={coachHere ? 'green' : 'default'}>
            {coachHere ? t('live.coachHere', 'coach is here') : t('live.coachWaiting', 'waiting for coach…')}
          </Tag>
        </div>

        {phase === 'idle' && (
          <Card>
            <p className="text-sm text-slate-600">
              {t(
                'live.hint',
                'Join the room and your coach sees your pitch live while you sing. They can send you exercises to do together — results show up for both of you instantly.',
              )}
            </p>
            <Button className="mt-3 w-full py-3" onClick={() => void join()}>
              🎤 {t('live.join', 'Join with microphone')}
            </Button>
          </Card>
        )}

        {phase === 'joining' && <Spinner label={t('live.joining', 'Opening microphone…')} />}

        {(phase === 'live' || phase === 'running') && (
          <VideoCallPanel
            call={call}
            otherLabel={lesson?.coachName ?? t('live.yourCoach', 'Your coach')}
            waitingText={
              coachHere
                ? t('call.connecting', 'Connecting video…')
                : t('live.coachWaiting', 'waiting for coach…')
            }
          />
        )}

        {phase === 'running' && runInfo && (
          <>
            {showVisualizer ? (
              <PitchCanvas
                rendered={runInfo.rendered}
                trace={runTrace}
                currentTime={runTime}
                mode="scroll"
                height={260}
                showTargets={!runInfo.isEcho}
              />
            ) : (
              <p className="rounded-xl bg-slate-100 px-3 py-6 text-center text-sm text-slate-600">
                🙈 {t('live.visualizerOffRun', 'Pitch display is off — sing by ear, your coach still sees your trace.')}
              </p>
            )}
            <p className="text-center text-sm text-slate-500">
              {runTime < 0
                ? t('player.countIn', 'Count-in…')
                : runInfo.isEcho
                  ? t('live.echoRunning', 'Echo the phrases you hear 👂→🎤')
                  : t('live.running', 'Sing along — your coach is watching live 🎯')}
            </p>
            <Button variant="secondary" className="w-full" onClick={finishExercise}>
              {t('player.finishEarly', 'Finish early')}
            </Button>
          </>
        )}

        {phase === 'live' && (
          <>
            {showVisualizer ? (
              <Card
                title={t('live.yourPitch', 'Your live pitch')}
                actions={lastScore !== null ? <Tag tone="brand">{t('live.lastScore', 'last run:')} {lastScore}</Tag> : undefined}
              >
                {freePoints.length === 0 ? (
                  <p className="py-10 text-center text-sm text-slate-400">
                    {t('live.singSomething', 'Sing or hum — your coach sees the same trace.')}
                  </p>
                ) : (
                  <LiveTraceCanvas points={freePoints} now={freeNow} height={240} />
                )}
              </Card>
            ) : (
              <p className="rounded-xl bg-slate-100 px-3 py-4 text-center text-sm text-slate-600">
                🙈 {t('live.visualizerOff', 'Your coach turned the pitch display off — they still see your trace live.')}
              </p>
            )}
            <p className="text-center text-xs text-slate-400">
              {t('live.exercisesArrive', 'When your coach sends an exercise it starts here automatically, with a count-in.')}
            </p>
          </>
        )}

        <ErrorText error={error} />
      </main>
    </div>
  );
}
